import { useEffect, useState } from "react"
import { Tooltip } from "antd"
import { SidebarToggleIcon } from "components/ui/customWebviewer/ElevationReferencesSidebar/icons/SidebarToggleIcon"
import { PageIcon } from "components/ui/customWebviewer/ElevationReferencesSidebar/icons/PageIcon"
import { ErefStatusIcon } from "components/ui/customWebviewer/ElevationReferencesSidebar/icons/ErefStatusIcon"
import { FurnitureStatusIcon } from "components/ui/customWebviewer/ElevationReferencesSidebar/icons/FurnitureStatusIcon"
import { useGroupedElevationData } from "components/ui/customWebviewer/ElevationReferencesSidebar/useGroupedElevationData.hook"
import { CollapseElevations } from "components/ui/collapse/CollapseElevations/CollapseElevations"
import { FurnitureModelLabel } from "components/ui/customWebviewer/FurnitureModelLabel/FurnitureModelLabel"
import { compactTooltipStyles, getShadowTooltipContainer } from "components/ui/customWebviewer/shared/shadowTooltip"
import { STATUS_LABEL } from "types/elevationReferences.type"
import type { TFurniture, TPageElevationData, TErefWithFurniture } from "types/elevationReferences.type"
import style from "./ElevationReferencesSidebar.module.scss"

const {
	sidebar,
	header,
	headerTitle,
	closeButton,
	content,
	placeholder,
	collapseLabel,
	collapseLabelButton,
	pageLabel,
	pageName,
	pageText,
	furnitureItem,
	furnitureItemButton,
	pageSection,
	furnitureList,
	unassignedLabel,
	statusIcon,
} = style

const EMPTY_RELATED_OBJECTS: TPageElevationData[] = []

type Props = {
	onClose?: () => void
	onErefClick?: (eref: TErefWithFurniture) => void
	onFurnitureClick?: (furniture: TFurniture) => void
	relatedObjects?: TPageElevationData[]
	selectedErefPageNumber?: number | null
}

export const ElevationReferencesSidebar = ({
	onClose,
	onErefClick,
	onFurnitureClick,
	relatedObjects = EMPTY_RELATED_OBJECTS,
	selectedErefPageNumber = null,
}: Props) => {
	const groupedData = useGroupedElevationData(relatedObjects)
	const [openPageNumbers, setOpenPageNumbers] = useState<Set<number>>(new Set())

	useEffect(() => {
		if (selectedErefPageNumber == null) return
		setOpenPageNumbers((prev) => {
			if (prev.has(selectedErefPageNumber)) return prev
			const next = new Set(prev)
			next.add(selectedErefPageNumber)
			return next
		})
	}, [selectedErefPageNumber])

	const renderFurnitureList = (furniture: TFurniture[]) => {
		return (
			<div className={furnitureList}>
				{furniture.map((item) => {
					const status = item.status ?? "work_in_progress"
					return (
						<button
							key={item.id}
							type="button"
							className={`${furnitureItem} ${furnitureItemButton}`}
							data-hotkey-item="furniture"
							onClick={() => onFurnitureClick?.(item)}
						>
							<Tooltip
								title={STATUS_LABEL[status]}
								placement="top"
								getPopupContainer={getShadowTooltipContainer}
								styles={compactTooltipStyles}
							>
								<span className={statusIcon}>
									<FurnitureStatusIcon status={status} />
								</span>
							</Tooltip>
							<FurnitureModelLabel item={item} />
						</button>
					)
				})}
			</div>
		)
	}

	const renderElevationReferences = () => {
		return groupedData.map((page) => {
			const erefItems = page.erefs.map((eref) => {
				const status = eref.status ?? "work_in_progress"
				return {
					label: (
						// A real <button>: it is the eref's click/activate target, so it gets native Enter
						// activation + focusability rather than a faked role="button". The Enter hotkey still
						// drives focus into the right sidebar (see useEditorHotkeys).
						<button
							type="button"
							className={`${collapseLabel} ${collapseLabelButton}`}
							data-hotkey-item="eref"
							onClick={(e) => {
								e.stopPropagation()
								onErefClick?.(eref)
							}}
						>
							<Tooltip
								title={STATUS_LABEL[status]}
								placement="top"
								getPopupContainer={getShadowTooltipContainer}
								styles={compactTooltipStyles}
							>
								<span className={statusIcon}>
									<ErefStatusIcon status={status} />
								</span>
							</Tooltip>
							{eref.elevation_number ?? "Unknown"} — {page.page_name ?? "Unknown"}
						</button>
					),
					key: eref.id,
					// Only the chevron toggles the panel, so the label span itself is the keyboard
					// tab stop (role=button) for selecting the eref — it doesn't compete with the
					// Ant collapse header's own Enter-to-toggle.
					collapsible: "icon" as const,
					children:
						eref.furniture.length > 0 ? (
							renderFurnitureList(eref.furniture)
						) : (
							<span className={furnitureList}>No furniture</span>
						),
				}
			})

			const unassignedItem =
				page.unassignedFurniture.length > 0
					? [
							{
								label: <span className={unassignedLabel}>Objects not connected to elevation</span>,
								key: "unassigned",
								children: renderFurnitureList(page.unassignedFurniture),
							},
						]
					: []

			return (
				<div key={page.page_number} className={pageSection}>
					<CollapseElevations
						activeKey={openPageNumbers.has(page.page_number) ? [page.page_number] : []}
						onChange={(keys) => {
							const list = Array.isArray(keys) ? keys : [keys]
							const isOpen = list.some((k) => Number(k) === page.page_number)
							setOpenPageNumbers((prev) => {
								const next = new Set(prev)
								if (isOpen) next.add(page.page_number)
								else next.delete(page.page_number)
								return next
							})
						}}
						items={[
							{
								label: (
									<span className={pageLabel}>
										<span className={collapseLabel}>
											<PageIcon />
											<span className={pageText}>Page {page.page_number + 1}</span>
										</span>
										<span className={pageName}>{page.page_name ?? "Unknown"}</span>
									</span>
								),
								key: page.page_number,
								children: <CollapseElevations items={[...erefItems, ...unassignedItem]} />,
							},
						]}
					/>
				</div>
			)
		})
	}

	return (
		<div className={sidebar} data-hotkey-sidebar tabIndex={-1}>
			<div className={header}>
				<span className={headerTitle}>Elevation references</span>
				<button className={closeButton} onClick={onClose} aria-label="Close sidebar">
					<SidebarToggleIcon />
				</button>
			</div>
			<div className={content} data-hotkey-content>
				{groupedData.length === 0 ? <p className={placeholder}>No references yet</p> : renderElevationReferences()}
			</div>
		</div>
	)
}
